"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { isActiveMember } from "@/lib/permissions";

export default function InactiveMemberBanner() {
    const { user } = useWorkspace();
    const pathname = usePathname();
    const [isDismissed, setIsDismissed] = useState(false);

    // Reset dismissed state whenever the user navigates/path changes
    useEffect(() => {
        setIsDismissed(false);
    }, [pathname]);

    if (!user || isActiveMember(user) || isDismissed) return null;

    // Suppress banner on any gigs-related routes
    if (pathname?.includes("/gigs")) return null;

    const handleDismiss = () => {
        setIsDismissed(true);
    };

    return (
        <div
            role="alert"
            className="relative w-full bg-[#09090b] border-b-3 border-[#27272a] px-4 py-3.5 sm:px-6 sm:py-3 flex items-start sm:items-center gap-3.5 transition-all duration-300"
            style={{ fontFamily: "'Outfit', 'DM Sans', system-ui, sans-serif" }}
        >
            {/* Icon */}
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0 mt-0.5 sm:mt-0"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>

            {/* Content area: stacks on mobile, horizontal on md+ screens */}
            <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4 pr-6 sm:pr-8">
                {/* Message */}
                <p className="text-xs sm:text-[13px] md:text-sm text-zinc-100 font-light leading-relaxed m-0 max-w-4xl">
                    <strong className="font-bold text-white">Your membership is currently inactive.</strong>{" "}
                    You may view your profile, but interactive features — including group management,
                    event participation, and platform tools — are not available at this time. To
                    regain full access, please renew your CMT Association membership. Access will be
                    restored within 4 hours of a successful renewal.
                </p>

                {/* CTA */}
                <div className="flex-shrink-0">
                    <a
                        href="https://cmtassociation.org/profile/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 bg-[#007a8a] hover:bg-[#005f6b] text-white text-xs sm:text-sm font-bold rounded-lg shadow-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap cursor-pointer"
                    >
                        Renew Membership
                    </a>
                </div>
            </div>

            {/* Close Button */}
            <button
                type="button"
                onClick={handleDismiss}
                className="absolute top-3 right-3 sm:top-1/2 sm:-translate-y-1/2 p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all duration-200 cursor-pointer"
                aria-label="Dismiss banner"
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>
    );
}

